import { Box, Icon, Tab, Tabs, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { BackgroundPlans, Pattern } from 'assets'
import { LandingButton } from 'components'
import { COLORS } from 'const'
import { AnimatePresence, motion } from 'framer-motion'
import { useDataLanding } from 'hooks'
import { useState } from 'react'
import { containerVariants, landingButtonPrimary, normalTextStyle, titleTextStyle } from 'styles'

const tabVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
    scale: 0.95,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
    scale: 0.95,
  }),
}
const whatsappLink = process.env.REACT_APP_WHATSAPP_LINK!

export const PlansComparasionSection = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { content } = useDataLanding()
  const [tabValue, setTabValue] = useState(0)
  const [direction, setDirection] = useState(0)

  const plans = content?.plans?.comparison.plans
  const features = content?.plans?.comparison.features

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDirection(newValue > tabValue ? 1 : -1)
    setTabValue(newValue)
  }

  return (
    <Box
      id="planComparison"
      sx={{
        position: 'relative',
        px: { xs: 2, md: 10, xl: 3 },
        py: { xs: 10, md: 14 },
        backgroundImage: `url(${BackgroundPlans})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'white',
        textAlign: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1,
        },
        '& > *': {
          position: 'relative',
          zIndex: 2,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
        component={motion.div}
        initial="offscreen"
        whileInView="onscreen"
        variants={containerVariants}
        viewport={{ once: true }}
      >
        <Typography
          variant="h3"
          sx={{
            ...titleTextStyle,
            fontFamily: 'Poppins',
            fontWeight: 600,
            mb: 2,
          }}
        >
          {content?.plans?.title}
        </Typography>

        <Typography
          sx={{
            ...normalTextStyle,
            fontFamily: 'Work Sans',
            maxWidth: { xs: 750, lg: 1300 },
            mx: 'auto',
            mb: 6,
            lineHeight: 1.6,
          }}
        >
          {content?.plans?.description}
        </Typography>

        {isMobile ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                mb: 3,
                '& .MuiTabs-indicator': {
                  backgroundColor: '#C5E04C',
                },
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontFamily: 'Work Sans',
                  fontSize: '14px',
                  '&.Mui-selected': {
                    color: '#C5E04C',
                  },
                },
              }}
              variant="fullWidth"
            >
              {plans?.map((plan, index) => (
                <Tab key={index} label={plan} wrapped={index === 1} />
              ))}
            </Tabs>

            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={tabValue}
                custom={direction}
                variants={tabVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.4,
                  ease: 'easeInOut',
                }}
                style={{ width: '100%' }}
              >
                <Box
                  id={`plan-${tabValue}`}
                  sx={{
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(15px)',
                    borderRadius: 4,
                    p: 3,
                    textAlign: 'left',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${Pattern})`,
                      backgroundSize: 'repeat',
                      backgroundPosition: 'center',
                      opacity: 1,
                      zIndex: -1,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ fontFamily: 'Work Sans', mb: 3, textAlign: 'center', color: '#ffffffff' }}
                  >
                    {plans ? plans[tabValue] : null}
                  </Typography>
                  {features?.map((feature, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        py: 1,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: feature.values[tabValue] ? '#C5E04C' : '#9E9E9E',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          boxShadow: feature.values[tabValue]
                            ? '0 1px 8px rgba(197, 224, 76, 0.3)'
                            : '0 1px 8px rgba(158, 158, 158, 0.3)',
                        }}
                      >
                        <Icon
                          sx={{
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                          }}
                        >
                          {feature.values[tabValue] ? 'check' : 'close'}
                        </Icon>
                      </Box>
                      <Typography sx={{ fontSize: 15, fontFamily: 'Work Sans', color: 'white' }}>
                        {feature.label}
                      </Typography>
                    </Box>
                  ))}
                  <LandingButton
                    text={content?.plans?.comparison.buttonText || 'CONTRATAR SERVICIO'}
                    style={{
                      ...landingButtonPrimary,
                      mt: 3,
                      width: '100%',
                      borderRadius: 30,
                      backgroundColor: COLORS.primary.main,
                      color: 'white',
                    }}
                    onClick={() => console.log(`Contratar ${plans ? plans[tabValue] : null}`)}
                  />
                </Box>
              </motion.div>
            </AnimatePresence>
          </Box>
        ) : (
          <Box
            id="plansComparisonTable"
            sx={{
              position: 'relative',
              borderRadius: '16px',
              background: 'rgba(0, 0, 0, 0.23)',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflowX: 'auto',
              mt: 4,
              width: { xs: '100%', md: '90%', xl: '100%' },
              maxWidth: { md: '1200px', lg: '85%' },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${Pattern})`,
                backgroundSize: 'repeat',
                backgroundPosition: 'center',
                opacity: 1,
                zIndex: -1,
              },
            }}
          >
            <Box sx={{ display: 'flex' }}>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderBottom: '1px solid rgba(255,255,255,0.25)',
                  borderRight: '1px solid rgba(255,255,255,0.25)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderTopLeftRadius: '15px',
                }}
              >
                <Typography
                  sx={{
                    ...normalTextStyle,
                    fontFamily: 'Work Sans',
                    fontWeight: 700,
                    color: 'white',
                  }}
                >
                  Beneficios
                </Typography>
              </Box>
              {plans?.map((plan, idx) => (
                <Box
                  key={idx}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.25)',
                    borderRight:
                      idx < plans?.length - 1 ? '1px solid rgba(255, 255, 255, 0.25)' : 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderTopRightRadius: idx === plans?.length - 1 ? '15px' : 0,
                  }}
                >
                  <Typography
                    sx={{
                      ...normalTextStyle,
                      fontFamily: 'Work Sans',
                      fontWeight: 700,
                      color: '#ffffffff',
                      textAlign: 'center',
                    }}
                  >
                    {plan}
                  </Typography>
                </Box>
              ))}
            </Box>

            {features?.map((feature, i) => (
              <Box key={i} sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    flex: 1,
                    px: 2,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.25)',
                    borderRight: '1px solid rgba(255,255,255,0.25)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',

                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      ...normalTextStyle,
                      fontFamily: 'Work Sans',
                      color: 'white',
                      textAlign: 'left',
                    }}
                  >
                    {feature.label}
                  </Typography>
                </Box>
                {feature.values.map((val, j) => (
                  <Box
                    key={j}
                    sx={{
                      flex: 1,
                      p: 2,
                      borderBottom: '1px solid rgba(255,255,255,0.25)',
                      borderRight:
                        j < feature.values.length - 1
                          ? '1px solid rgba(255, 255, 255, 0.25)'
                          : 'none',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 24, lg: 30 },
                        height: { xs: 24, lg: 30 },
                        borderRadius: '50%',
                        backgroundColor: val ? '#C5E04C' : '#9E9E9E',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: val
                          ? '0 1px 8px rgba(197, 224, 76, 0.3)'
                          : '0 1px 8px rgba(158, 158, 158, 0.3)',
                      }}
                    >
                      <Icon
                        sx={{
                          color: 'white',
                          fontSize: { xs: '14px', lg: '30px' },
                          fontWeight: 'bold',
                        }}
                      >
                        {val ? 'check' : 'close'}
                      </Icon>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}

            <Box sx={{ display: 'flex' }}>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRight: '1px solid rgba(255,255,255,0.25)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  minHeight: 80,
                  borderBottomLeftRadius: '15px',
                }}
              />
              {plans?.map((plan, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRight:
                      i < plans?.length - 1 ? '1px solid rgba(255,255,255,0.25)' : 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottomRightRadius: i === plans?.length - 1 ? '15px' : 0,
                  }}
                >
                  <LandingButton
                    text={content?.plans?.comparison.buttonText || 'CONTRATAR SERVICIO'}
                    style={{
                      ...landingButtonPrimary,
                      borderRadius: 30,
                      fontSize: '12px',
                      py: 1,
                    }}
                    onClick={() => {
                      window.open(whatsappLink, '_blank')
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
